import React from 'react';
import {Button, Checkbox, Modal, ModalBody, ModalFooter, ModalHeader, Radio} from 'react-bootstrap';
import FeaturesState from '../../domain/FeaturesState';
import {batch, connect} from 'react-redux';
import {
    copyFeatureGeometries, GeneratePossibleCopyOperations,
    generatePossibleCopyOperations,
    setCopyGeometryVisible,
    setPossibleCopiableFeatures
} from '../../../../actions/actions';
import {CopiableGeometryFeature, DisplayFeature, NvdbFeature} from '../../domain';
import * as selectors from '../../selectors';
import {Spinner} from '../../../../shared-components/status/Spinner';

export type CopyGeometryDirection = 'TO' | 'FROM' | 'UNKNOWN';

interface TagProps { }
interface DispatchProps {
    toggleModal: (visible: boolean) => void;
    clearCopiableFeatures: () => void;
    updateCopiableFeatures: GeneratePossibleCopyOperations;
    copyGeometries: (from: string, to: string[]) => void;
}
interface StateProps {
    visible: boolean;
    selectedFeatures: DisplayFeature[];
    features?: DisplayFeature[];
    copiableGeometries?: CopiableGeometryFeature[];
    fetchingCopiableFeatureGeometries: boolean;
    copyingGeometryFeatures: boolean;
    direction: CopyGeometryDirection;
}

type CopyGeometryProps = TagProps & DispatchProps & StateProps;

interface CopyGeometryState {
    toCopy: Set<string>;
}

const mapStateToProps = (state: FeaturesState): StateProps => ({
    visible: selectors.isCopyGeometryModalVisible(state),
    selectedFeatures: selectors.selectedFeatures(state),
    features: selectors.features(state),
    copiableGeometries: selectors.copiableGeometries(state),
    fetchingCopiableFeatureGeometries: selectors.isFetchingCopiableFeatureGeometries(state),
    copyingGeometryFeatures: selectors.isCopyingFeatureGeometries(state),
    direction: selectors.getGeometryCopyDirection(state)
});

const mapDispatchToProps: DispatchProps = {
    toggleModal: setCopyGeometryVisible,
    clearCopiableFeatures: () => setPossibleCopiableFeatures([]),
    updateCopiableFeatures: generatePossibleCopyOperations,
    copyGeometries: copyFeatureGeometries
};

class CopyGeometryModal extends React.Component<CopyGeometryProps, CopyGeometryState> {
    constructor(props: CopyGeometryProps) {
        super(props);
        this.state = {
            toCopy: new Set<string>()
        };
        this.handleClose = this.handleClose.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<CopyGeometryProps>, prevState: Readonly<CopyGeometryState>) {
        if (!prevProps.visible && this.props.visible && this.props.direction !== 'UNKNOWN' && this.props.selectedFeatures.length > 0) {
            this.setState({toCopy: new Set<string>()});
            this.props.updateCopiableFeatures();
        }
        if (prevProps.copyingGeometryFeatures && !this.props.copyingGeometryFeatures) {
            this.handleClose();
        }
    }

    handleClose() {
        batch(() => {
            this.props.clearCopiableFeatures();
            this.props.toggleModal(false);
        });
        this.setState({toCopy: new Set<string>()});
    }

    handleSubmit() {
        if (this.props.direction === 'TO' && this.state.toCopy.size === 1) {
            this.props.copyGeometries(Array.from(this.state.toCopy)[0], this.props.selectedFeatures.map(selected => selected.id));
        } else if (this.props.direction === 'FROM' && this.props.selectedFeatures.length === 1) {
            this.props.copyGeometries(this.props.selectedFeatures[0].id, Array.from(this.state.toCopy));
        }
    }

    handleClickFeature(id: string) {
        const selected = this.state.toCopy.has(id);
        if (this.props.direction === 'FROM') {
            const newSet = new Set(this.state.toCopy);
            if (selected) {
                newSet.delete(id);
                this.setState({toCopy: newSet});
            } else {
                newSet.add(id);
                this.setState({toCopy: newSet});
            }
        } else {
            if (selected) {
                this.setState({toCopy: new Set<string>()});
            } else {
                this.setState({toCopy: new Set<string>([id])});
            }
        }
    }

    render() {
        return (
            <Modal show={this.props.visible} onHide={this.handleClose} animation={false}>
                <ModalHeader>Kopiering av geometri mellom objekter</ModalHeader>
                <ModalBody>
                    {
                        this.props.fetchingCopiableFeatureGeometries
                            ? <Spinner/>
                            : this.props.direction !== 'UNKNOWN'
                            ? <div className={'flex-container flex-column'}>
                                    <b>{this.props.copiableGeometries.length === 0 ? ('Ingen gyldige kopierbare objekter for de du har valgt') : this.props.direction === 'FROM'
                                            ? 'Hvilke objekter ønsker du å kopiere valgt geometri til?'
                                            : 'Hvilket objekt ønsker du å kopiere geometrien fra?'}</b>
                                    {
                                        this.props.copiableGeometries.map(feature =>
                                            this.props.direction === 'FROM'
                                                ? <Checkbox key={feature.id} checked={this.state.toCopy.has(feature.id)} onChange={() => this.handleClickFeature(feature.id)}>{feature.name}</Checkbox>
                                                : <Radio key={feature.id} checked={this.state.toCopy.has(feature.id)} onChange={() => this.handleClickFeature(feature.id)}>{feature.name}</Radio>

                                        )
                                    }
                                </div>
                            : <p>Du må velge minimum et vegobjekt for å bruke denne modalen.</p>
                    }
                </ModalBody>
                <ModalFooter>
                    {
                        this.props.copiableGeometries.length > 0 && `${this.state.toCopy.size} objekt${this.state.toCopy.size === 1 ? '' : 'er'} valgt for kopiering`
                    }
                    <Button onClick={this.handleClose}>Avbryt</Button>
                    <Button onClick={this.handleSubmit} disabled={this.state.toCopy.size === 0}>OK</Button>
                </ModalFooter>
            </Modal>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CopyGeometryModal);